import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../auth/AuthProvider'
import {
  Target,
  Users,
  Building2,
  Zap,
  Workflow,
  Bell,
  Sparkles,
  Bot,
  Code2,
  Database,
  Layers,
  Radio,
  Check,
  ChevronRight,
  Sun,
  Moon,
} from 'lucide-react'
import { useTheme } from '../../providers/ThemeProvider'
import { useTranslation } from 'react-i18next'
import { SELF_SIGNUP_ENABLED } from '@edition/ui'
import { NavSignUpButton, HeroGetStartedButton, BottomGetStartedButton } from '@edition/controls'

export function LandingPage() {
  const { login, isAuthenticated } = useAuth()
  const { resolvedTheme, setTheme } = useTheme()
  const navigate = useNavigate()
  const { t } = useTranslation()

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true })
    }
  }, [isAuthenticated, navigate])

  if (isAuthenticated) {
    return null
  }

  const levels = [
    {
      icon: Building2,
      title: t('landing.levels.execTitle'),
      desc: t('landing.levels.execDesc'),
      accent: 'bg-primary/10 text-primary',
    },
    {
      icon: Layers,
      title: t('landing.levels.leaderTitle'),
      desc: t('landing.levels.leaderDesc'),
      accent: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    },
    {
      icon: Users,
      title: t('landing.levels.teamTitle'),
      desc: t('landing.levels.teamDesc'),
      accent: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    },
    {
      icon: Radio,
      title: t('landing.levels.stakeholderTitle'),
      desc: t('landing.levels.stakeholderDesc'),
      accent: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
    },
  ]

  const features = [
    {
      icon: Workflow,
      title: t('landing.features.sankey'),
      desc: t('landing.features.sankeyDesc'),
      accent: 'bg-primary/10 text-primary',
    },
    {
      icon: Bell,
      title: t('landing.features.stakeholder'),
      desc: t('landing.features.stakeholderDesc'),
      accent: 'bg-violet-500/10 text-violet-600 dark:text-violet-400',
    },
    {
      icon: Sparkles,
      title: t('landing.features.aiStatus'),
      desc: t('landing.features.aiStatusDesc'),
      accent: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    },
    {
      icon: Bot,
      title: t('landing.features.mcp'),
      desc: t('landing.features.mcpDesc'),
      accent: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    },
    {
      icon: Code2,
      title: t('landing.features.apiFirst'),
      desc: t('landing.features.apiFirstDesc'),
      accent: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    },
    {
      icon: Database,
      title: t('landing.features.ingest'),
      desc: t('landing.features.ingestDesc'),
      accent: 'bg-rose-500/10 text-rose-600 dark:text-rose-400',
    },
  ]

  const platformPoints = [
    t('landing.platform.apiPoint'),
    t('landing.platform.mcpPoint'),
    t('landing.platform.aiPoint'),
    t('landing.platform.securityPoint'),
  ]

  const steps = [
    {
      step: '01',
      title: t('landing.steps.defineStrategy'),
      desc: t('landing.steps.defineStrategyDesc'),
    },
    {
      step: '02',
      title: t('landing.steps.cascadeGoals'),
      desc: t('landing.steps.cascadeGoalsDesc'),
    },
    {
      step: '03',
      title: t('landing.steps.trackProgress'),
      desc: t('landing.steps.trackProgressDesc'),
    },
  ]

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* Nav */}
      <nav className="fixed top-0 z-50 w-full border-b border-border/50 bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-md bg-primary flex items-center justify-center">
              <Target className="h-4 w-4 text-primary-foreground" />
            </div>
            <span className="text-lg font-bold tracking-tight font-mono">Gabai</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
              className="rounded-md p-2 text-muted-foreground hover:bg-accent/50 transition-colors"
              title={resolvedTheme === 'dark' ? t('auth.switchToLight') : t('auth.switchToDark')}
              aria-label={resolvedTheme === 'dark' ? t('auth.switchToLight') : t('auth.switchToDark')}
            >
              {resolvedTheme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <button
              onClick={() => login()}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              {t('auth.signIn')}
            </button>
            {SELF_SIGNUP_ENABLED && NavSignUpButton && <NavSignUpButton />}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 pb-24 bg-grid">
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent pointer-events-none" />
        <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-primary/5 blur-[120px] pointer-events-none" />

        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <div className="landing-stagger">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-4 py-1.5 text-xs font-medium text-primary mb-8">
              <Zap className="h-3 w-3" />
              {t('landing.tagline')}
            </div>

            <h1 className="text-5xl font-bold tracking-tight leading-[1.1] sm:text-6xl lg:text-7xl">
              {t('landing.heroTitle1')}
              <br />
              <span className="text-primary">{t('landing.heroTitle2')}</span>
            </h1>

            <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground leading-relaxed">
              {t('landing.heroSubtitle')}
            </p>

            <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              {SELF_SIGNUP_ENABLED && HeroGetStartedButton && <HeroGetStartedButton />}
              <a
                href="#features"
                className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                {t('auth.seeHowItWorks')}
                <ChevronRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Value at every level */}
      <section className="border-y border-border bg-card/50 py-24">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-primary font-mono uppercase tracking-wider">{t('landing.levels.label')}</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
              {t('landing.levels.title')}
            </h2>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
              {t('landing.levels.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {levels.map((level) => (
              <div
                key={level.title}
                className="rounded-lg bg-card p-6 border border-border shadow-sm"
              >
                <div className={`inline-flex rounded-lg p-2.5 ${level.accent}`}>
                  <level.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-base font-semibold">{level.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{level.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-primary font-mono uppercase tracking-wider">{t('landing.capabilities')}</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
              {t('landing.everythingYouNeed')}
            </h2>
            <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
              {t('landing.capabilitiesSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="group rounded-lg bg-card p-6 border border-border shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
              >
                <div className={`inline-flex rounded-lg p-2.5 ${feature.accent}`}>
                  <feature.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-base font-semibold">{feature.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open & AI-native platform */}
      <section className="py-24 bg-muted/30 border-y border-border">
        <div className="mx-auto max-w-6xl px-6">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            {/* Copy */}
            <div>
              <p className="text-sm font-semibold text-primary font-mono uppercase tracking-wider">{t('landing.platform.label')}</p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
                {t('landing.platform.title')}
              </h2>
              <p className="mt-4 text-muted-foreground">
                {t('landing.platform.subtitle')}
              </p>
              <ul className="mt-8 space-y-4">
                {platformPoints.map((point) => (
                  <li key={point} className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <Check className="h-3 w-3" />
                    </span>
                    <span className="text-sm text-foreground/90 leading-relaxed">{point}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Terminal / MCP demo */}
            <div className="rounded-xl border border-border bg-card shadow-xl shadow-primary/5 overflow-hidden">
              <div className="flex items-center gap-2 border-b border-border bg-muted/50 px-4 py-3">
                <div className="h-2.5 w-2.5 rounded-full bg-red-400/60" />
                <div className="h-2.5 w-2.5 rounded-full bg-amber-400/60" />
                <div className="h-2.5 w-2.5 rounded-full bg-green-400/60" />
                <span className="ml-2 text-xs font-mono text-muted-foreground">gabai · api</span>
              </div>
              <div className="p-5 font-mono text-[13px] leading-relaxed">
                <p className="text-muted-foreground">{t('landing.platform.terminalComment')}</p>
                <p className="mt-1">
                  <span className="text-primary">$</span> curl -X POST api.gabai.com/v1/check-ins \
                </p>
                <p className="pl-4 text-foreground/80">
                  -H <span className="text-amber-600 dark:text-amber-400">"Authorization: Bearer ck_live_…"</span> \
                </p>
                <p className="pl-4 text-foreground/80">
                  -d <span className="text-emerald-600 dark:text-emerald-400">{'\'{ "teamId": "…", "confidence": 0.8 }\''}</span>
                </p>
                <p className="mt-2 text-emerald-600 dark:text-emerald-400">✓ {t('landing.platform.terminalResult')}</p>

                <div className="my-4 border-t border-border" />

                <p className="text-muted-foreground">{t('landing.platform.mcpComment')}</p>
                <p className="mt-1">
                  <span className="text-primary">▸</span> getOrganizationHealthMatrix()
                </p>
                <p className="pl-4 text-foreground/80">{t('landing.platform.mcpResult')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 bg-muted/50 border-y border-border">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-center mb-16">
            <p className="text-sm font-semibold text-primary font-mono uppercase tracking-wider">{t('landing.process')}</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
              {t('landing.strategyToExecution')}
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-3">
            {steps.map((item) => (
              <div key={item.step} className="relative">
                <p className="text-5xl font-bold font-mono text-primary/15">{item.step}</p>
                <h3 className="mt-2 text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dashboard preview placeholder */}
      <section className="py-24">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-primary font-mono uppercase tracking-wider">{t('landing.dashboardLabel')}</p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
              {t('landing.dashboardTitle')}
            </h2>
          </div>

          <div className="rounded-xl border border-border bg-card p-2 shadow-xl shadow-primary/5">
            <div className="rounded-lg bg-muted/50 border border-border overflow-hidden">
              {/* Simulated dashboard */}
              <div className="p-6 space-y-4">
                {/* Top bar simulation */}
                <div className="flex items-center gap-3">
                  <div className="h-3 w-3 rounded-full bg-red-400/60" />
                  <div className="h-3 w-3 rounded-full bg-amber-400/60" />
                  <div className="h-3 w-3 rounded-full bg-green-400/60" />
                  <div className="ml-4 h-4 w-48 rounded bg-border/60" />
                </div>
                {/* Content simulation */}
                <div className="grid grid-cols-4 gap-3">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="rounded-md bg-card border border-border p-4">
                      <div className="h-3 w-16 rounded bg-muted-foreground/20 mb-2" />
                      <div className="h-6 w-12 rounded bg-primary/30 font-mono" />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-md bg-card border border-border p-4 h-40">
                    <div className="h-3 w-24 rounded bg-muted-foreground/20 mb-4" />
                    <div className="flex items-end gap-2 h-24">
                      {[40, 65, 45, 80, 55, 70, 90, 60].map((h, i) => (
                        <div
                          key={i}
                          className="flex-1 rounded-t bg-primary/30"
                          style={{ height: `${h}%` }}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="rounded-md bg-card border border-border p-4 h-40">
                    <div className="h-3 w-24 rounded bg-muted-foreground/20 mb-4" />
                    <div className="flex items-center justify-center h-24">
                      <div className="h-24 w-24 rounded-full border-8 border-primary/30 border-t-primary" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 relative">
        <div className="absolute inset-0 bg-gradient-to-t from-primary/5 via-transparent to-transparent pointer-events-none" />
        <div className="relative mx-auto max-w-3xl px-6 text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            {t('landing.readyToAlign')}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            {t('landing.readyToAlignSubtitle')}
          </p>
          {SELF_SIGNUP_ENABLED && BottomGetStartedButton && (
            <div className="mt-8">
              <BottomGetStartedButton onClick={() => login()} />
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="mx-auto max-w-6xl px-6 flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded bg-primary flex items-center justify-center">
              <Target className="h-3 w-3 text-primary-foreground" />
            </div>
            <span className="text-sm font-semibold font-mono">Gabai</span>
          </div>
          <p className="text-xs text-muted-foreground">
            &copy; {new Date().getFullYear()} Caisibo. {t('landing.allRightsReserved')}
          </p>
        </div>
      </footer>
    </div>
  )
}
